const post = {
  slug: 'how-long-should-a-youtube-script-be',
  cluster: 'scripting',
  eyebrow: 'Scripting',
  title: 'How long should a script be?',
  description:
    'Converting words to runtime, why the target length is the wrong thing to aim at, and what padding actually costs.',
  keywords: [
    'youtube script length', 'how many words per minute', 'video script word count',
    'ideal video length', 'script timing', 'words to minutes',
  ],
  published: '2026-08-21',
  hero: {
    src: '/blog/how-long-should-a-youtube-script-be.webp',
    alt: 'A word count converted to a runtime bar, with a padded section shaded as the part viewers leave during.',
  },
  lede:
    'The arithmetic is easy. The judgement is not — and aiming at a target length is one of the more reliable ways to make a worse video.',
  sections: [
    {
      heading: 'Words to minutes',
      body: [
        {
          type: 'para',
          text: 'Around **150 words per minute** is a reasonable planning figure for narration. It is a starting estimate, not a constant — delivery speed varies by narrator, by subject, and by how much you pause.',
        },
        {
          type: 'list',
          items: [
            '1,200 words ≈ 8 minutes',
            '1,500 words ≈ 10 minutes',
            '2,300 words ≈ 15 minutes',
          ],
        },
        {
          type: 'para',
          text: 'Two things push the real runtime longer than the arithmetic: pauses for the visuals to land, and any section where the pace deliberately slows. Budget for the estimate being a floor rather than an exact figure.',
        },
        {
          type: 'para',
          text: 'The reliable way to know is to read the draft aloud and time it. That takes as long as the video and is worth doing anyway — see [writing for the ear](/blogs/writing-a-script-for-narration).',
        },
      ],
    },
    {
      heading: 'Why the target length is the wrong target',
      body: [
        {
          type: 'para',
          text: 'The common approach: decide the video should be ten minutes, then write 1,500 words. The problem is that the idea has a natural size, and forcing it to a number damages it in one of two directions.',
        },
        {
          type: 'para',
          text: 'Padded to length, the video acquires stretches with nothing pending — which is exactly the slow mid-video bleed described in [diagnosing a channel](/blogs/youtube-channel-not-growing). Compressed to length, it drops the concrete moments and becomes a dense list of facts.',
        },
        {
          type: 'para',
          text: 'Write what the idea supports, then check the runtime is not wildly off what the format expects. If a topic genuinely only sustains four minutes, a four-minute video that holds is better than a ten-minute one that does not.',
        },
      ],
    },
    {
      heading: 'What padding actually costs',
      body: [
        {
          type: 'para',
          text: 'It is often justified by ad breaks — longer videos can carry more. The arithmetic usually does not work.',
        },
        {
          type: 'para',
          text: 'Padding lowers retention, retention affects how much the video gets recommended, and recommendation drives the view count that the ad rate is multiplied by. Trading the larger multiplier for the smaller one is a bad exchange, and it compounds: a video that underperforms teaches the system less about who to show your next one to.',
        },
        {
          type: 'quote',
          text: 'Total watch time is what matters. A shorter video people finish frequently beats a longer one they leave.',
        },
      ],
    },
    {
      heading: 'The cutting test',
      body: [
        {
          type: 'para',
          text: 'Go through the draft and, for each paragraph, ask: **if this were gone, would the video be worse?**',
        },
        {
          type: 'para',
          text: 'Not "is this true" or "is this interesting" — those are too easy to pass. Whether its absence would be noticed. Anything that fails is padding regardless of how well written it is, and the paragraphs that fail most often are the ones restating something already said in different words.',
        },
        {
          type: 'para',
          text: 'The other reliable cut: the second example. Where you gave two illustrations of the same point, one of them is almost always doing nothing.',
        },
      ],
    },
    {
      heading: 'A rough guide by format',
      body: [
        {
          type: 'para',
          text: 'Not rules — the length these formats tend to land at when written to fit their idea rather than to a target.',
        },
        {
          type: 'list',
          items: [
            '**A single mechanism explained**: usually 5-8 minutes. Longer means it is really two videos.',
            '**A case study or documentary treatment**: 10-20, because the narrative needs room to turn.',
            '**A list**: however many items × the time each needs, which is why lists bloat so easily.',
            '**A comparison**: 6-12. Comparisons get repetitive faster than they feel like they should while writing.',
          ],
        },
      ],
    },
  ],
  faq: [
    {
      q: 'Do longer videos rank better?',
      a: 'Length itself is not the lever — watch time and retention are, and length only helps if the video sustains it. A long video with poor retention performs worse than a short one with good retention.',
    },
    {
      q: 'How long should a script be for a short-form video?',
      a: 'Very roughly 30 words for 15 seconds at a normal pace, though short-form is usually delivered faster. The bigger difference is structural: there is no room for a setup, so the hook and the subject arrive together.',
    },
  ],
  related: ['writing-a-script-for-narration', 'youtube-video-script-structure', 'youtube-script-writing'],
};

export default post;
